import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { createWorkflowItem2MutationDocument } from '@liscio/api';
import { hasVisibleHtmlContent, useGraphQLClient } from '@liscio/common';

/** Re-export for callers that already import from this module. */
export {
  decodeFieldPrompt,
  getFieldDisplayParts,
} from '@liscio/common';

/** Uploaded document reference for firm-authored file steps (any file type, including images) */
export type FirmAuthoredFileRef = {
  id?: number;
  uuid: string;
  name: string;
  contentType: string;
};

/**
 * Field definition for a custom item.
 * Each field becomes a separate workflow item in the section.
 */
export type CustomFieldDefinition = {
  /** Unique client-side ID for the field (used in local state) */
  id: string;
  /** Label / title shown with the step */
  label: string;
  /** Optional description / instructions */
  description: string;
  /** Field type: text or file (file includes images and other uploads) */
  fieldType: 'text' | 'file';
  /** Whether the client must acknowledge the step */
  required: boolean;
  /** Firm-authored copy for text steps */
  textContent?: string;
  /** Firm-uploaded files for file steps */
  firmFiles?: FirmAuthoredFileRef[];
};

/** Maps frontend field types to workflow item type strings. */
const FIELD_TYPE_TO_WORKFLOW_TYPE: Record<
  CustomFieldDefinition['fieldType'],
  string
> = {
  text: 'WfiFirmAuthoredTextAcknowledgement',
  file: 'WfiReviewableDocument',
};

/**
 * Encodes label + required flag into `WorkflowItem#prompt`.
 * Legacy rows may still store label + newline + `---` + newline + description in `prompt`; `getFieldDisplayParts` reads those.
 */
export function encodeFieldPrompt(field: CustomFieldDefinition): string {
  const prefix = field.required ? '[REQUIRED] ' : '';
  return `${prefix}${field.label}`;
}

/** Extras for `options.firm_authored_content` only — document link is `documentId` (DB pk, same as non-custom `liscio_object`). */
function buildFirmAuthoredContent(field: CustomFieldDefinition) {
  const raw = field.textContent ?? '';
  const visible = hasVisibleHtmlContent(raw);
  if (field.fieldType === 'text') {
    return {
      contentType: field.fieldType,
      textContent: visible ? raw : '',
    };
  }

  return {
    contentType: field.fieldType,
    ...(visible ? { textContent: raw } : {}),
  };
}

function primaryFirmFileRef(field: CustomFieldDefinition) {
  return field.firmFiles?.[0];
}

/**
 * Hook for creating custom workflow items in a section.
 *
 * Provides a function to create multiple fields as separate workflow items
 * via the `createWorkflowItem2` GraphQL mutation.
 */
export function useCreateCustomItem() {
  const gqlClient = useGraphQLClient();
  const queryClient = useQueryClient();

  const { mutateAsync: createItemMutation, isLoading: isCreating } =
    gqlClient.useMutationGraphQL(createWorkflowItem2MutationDocument, {
      onError: () => {
        toast.error('Failed to create custom item.');
      },
    });

  /**
   * Create one or more custom fields as workflow items in the given section.
   *
   * @param workflowId     - The workflow ID to attach items to
   * @param sectionId      - The workflow (template) section ID to add items into
   * @param fields         - Array of field definitions
   * @returns Promise that resolves when all items have been created
   */
  const createCustomFields = useCallback(
    async (
      workflowId: string,
      sectionId: string,
      fields: CustomFieldDefinition[]
    ) => {
      const results: Array<{ id: string; prompt: string }> = [];

      for (const field of fields) {
        const prompt = encodeFieldPrompt(field);
        const workflowItemType = FIELD_TYPE_TO_WORKFLOW_TYPE[field.fieldType];
        const firmAuthoredContent = buildFirmAuthoredContent(field);
        const fileRef = primaryFirmFileRef(field);
        const fileCount = field.firmFiles?.length ?? 0;
        const documentId =
          field.fieldType === 'file' && fileRef?.id != null
            ? fileRef.id
            : undefined;

        if (field.fieldType === 'file' && fileCount > 1) {
          toast.error(
            `Only one file is supported for "${field.label}". Remove extra files and try again.`
          );
          continue;
        }

        if (field.fieldType === 'file' && documentId == null) {
          toast.error(
            `Missing document id for "${field.label}". Re-upload the file and try again.`
          );
          continue;
        }

        try {
          const response = await createItemMutation({
            prompt,
            workflowItemType,
            workflowId,
            workflowSectionId: sectionId,
            firmAuthoredContent,
            documentId,
          });

          if (response.createWorkflowItem2?.errors?.length) {
            const errMsg = response.createWorkflowItem2.errors[0].message;
            toast.error(`Error creating field "${field.label}": ${errMsg}`);
            continue;
          }

          const createdItem = response.createWorkflowItem2?.workflowItem;
          if (createdItem) {
            results.push({
              id: createdItem.id,
              prompt: createdItem.prompt || '',
            });
          }
        } catch {
          toast.error(`Failed to create field "${field.label}".`);
        }
      }

      queryClient.invalidateQueries(['workflowRequest']);
      queryClient.invalidateQueries(['items']);

      if (results.length === fields.length) {
        toast.success(
          results.length === 1
            ? 'Custom item created!'
            : `${results.length} custom items created!`
        );
      }

      return results;
    },
    [createItemMutation, queryClient]
  );

  return { createCustomFields, isCreating };
}
